/**
 * DID management (brief §5 Phase 3, §4D).
 *
 * Table of DIDs with assignment to pairs. Assignment opens the blocking
 * carrier-instruction step: the operator must acknowledge the carrier-side
 * change before the platform records the move (the backend rejects an
 * assignment without `carrier_ack`). CSV import is dry-run-first; export is
 * a same-origin GET.
 */

import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  acknowledgeDidFinding,
  adoptDidFinding,
  assignDid,
  confirmDid,
  didsExportUrl,
  dismissDidFinding,
  getDidFindings,
  getDids,
  getFailoverReadiness,
  getPairs,
  importDids,
  runDidDiscovery,
} from "@pbxmgr/api";
import type { DidFindingRow, DidImportReport, DidRow, ExtensionPairRow } from "@pbxmgr/api";
import { Alert, Badge, Button, Spinner } from "@pbxmgr/ui";
import type { BadgeTone } from "@pbxmgr/ui";

import { usePermission } from "../auth/usePermission";
import { Panel } from "../dashboard/panels";

function ConfirmCell({ did, onDone }: { did: DidRow; onDone: () => void }): ReactNode {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (body: { carrier?: boolean; e911?: boolean }) => confirmDid(did.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["dids"] });
      void qc.invalidateQueries({ queryKey: ["did-readiness"] });
      onDone();
    },
  });
  // E911 only applies to a DID routed to a phone pair.
  const e911Applies = did.pair_id !== null;
  return (
    <div className="did-confirm">
      <label
        className="did-assign__ack"
        title={did.carrier_confirmed_by ? `by ${did.carrier_confirmed_by}` : "not confirmed"}
      >
        <input
          type="checkbox"
          checked={did.carrier_confirmed}
          disabled={set.isPending}
          onChange={(e) => set.mutate({ carrier: e.target.checked })}
        />
        Carrier{did.needs?.carrier ? " ⚠" : ""}
      </label>
      {e911Applies ? (
        <label
          className="did-assign__ack"
          title={did.e911_confirmed_by ? `by ${did.e911_confirmed_by}` : "not confirmed"}
        >
          <input
            type="checkbox"
            checked={did.e911_confirmed}
            disabled={set.isPending}
            onChange={(e) => set.mutate({ e911: e.target.checked })}
          />
          E911{did.needs?.e911 ? " ⚠" : ""}
        </label>
      ) : null}
    </div>
  );
}

const DID_TONE: Record<string, BadgeTone> = {
  assigned: "online",
  unassigned: "unknown",
  pending: "degraded",
  retired: "offline",
};

function AssignForm({
  did,
  pairs,
  onDone,
}: {
  did: DidRow;
  pairs: ExtensionPairRow[];
  onDone: () => void;
}): ReactNode {
  const [pairId, setPairId] = useState<string>(did.pair_id ? String(did.pair_id) : "");
  const [ack, setAck] = useState(false);
  const assign = useMutation({
    mutationFn: () => assignDid(did.id, pairId ? Number(pairId) : null, ack),
    onSuccess: onDone,
  });

  return (
    <tr className="did-assign">
      <td colSpan={7}>
        <div className="did-assign__box">
          <p>
            <strong>Carrier step required:</strong> route <strong>{did.number}</strong> at the
            carrier to the target node before confirming here — the platform records the
            assignment, it does not move carrier routing.
          </p>
          <div className="did-assign__controls">
            <label htmlFor={`assign-pair-${did.id}`}>Pair</label>
            <select
              id={`assign-pair-${did.id}`}
              value={pairId}
              onChange={(e) => setPairId(e.target.value)}
            >
              <option value="">— unassign —</option>
              {pairs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.primary_ext} (node {p.primary_node_id} → {p.backup_node_id})
                </option>
              ))}
            </select>
            <label className="did-assign__ack">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              Carrier change completed / acknowledged
            </label>
            <Button disabled={!ack || assign.isPending} onClick={() => assign.mutate()}>
              Record assignment
            </Button>
          </div>
          {assign.isError ? <Alert>Assignment rejected — is the carrier step acknowledged?</Alert> : null}
        </div>
      </td>
    </tr>
  );
}

const FINDING_LABEL: Record<DidFindingRow["kind"], string> = {
  unknown_number: "On the fleet, not in the platform",
  missing_on_nodes: "In the platform, nowhere on the fleet",
  placement_mismatch: "Assigned pair doesn't match where it's carried",
};

function FindingCard({ finding, onDone }: { finding: DidFindingRow; onDone: () => void }): ReactNode {
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = (): void => {
    setError(null);
    onDone();
  };
  const fail = (err: unknown): void =>
    setError(err instanceof Error ? err.message : "request failed");
  const adopt = useMutation({
    mutationFn: () => adoptDidFinding(finding.id),
    onSuccess: done,
    onError: fail,
  });
  const acknowledge = useMutation({
    mutationFn: () => acknowledgeDidFinding(finding.id, confirmed),
    onSuccess: done,
    onError: fail,
  });
  const dismiss = useMutation({
    mutationFn: () => dismissDidFinding(finding.id),
    onSuccess: done,
    onError: fail,
  });

  const adoptable = finding.kind === "unknown_number" && finding.did_id === null;
  return (
    <div className="did-finding">
      <div>
        <Badge tone={finding.kind === "unknown_number" ? "degraded" : "offline"}>
          {finding.kind.replace(/_/g, " ")}
        </Badge>{" "}
        <strong>{finding.number}</strong> — {FINDING_LABEL[finding.kind]}
        {finding.status === "acknowledged" ? (
          <Badge tone="online">acknowledged by {finding.ack_by}</Badge>
        ) : null}
      </div>
      {finding.evidence.length > 0 ? (
        <ul>
          {finding.evidence.map((e, i) => (
            <li key={i}>
              <code>{e.raw}</code> as {e.field} on {e.account}@{e.domain} (node {e.node_id})
            </li>
          ))}
        </ul>
      ) : null}
      {finding.instructions ? <p className="did-finding__instructions">{finding.instructions}</p> : null}
      {finding.status === "open" ? (
        <div className="pairing-actions">
          {adoptable ? (
            <Button disabled={adopt.isPending} onClick={() => adopt.mutate()}>
              Adopt (backfills only verified fields)
            </Button>
          ) : null}
          {finding.instructions ? (
            <>
              <label className="did-assign__ack">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                I completed and verified these steps
              </label>
              <Button
                variant="secondary"
                disabled={!confirmed || acknowledge.isPending}
                onClick={() => acknowledge.mutate()}
              >
                Acknowledge
              </Button>
            </>
          ) : null}
          <Button variant="secondary" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>
            Dismiss
          </Button>
        </div>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

function DiscoveryPanel({ onChanged }: { onChanged: () => void }): ReactNode {
  const qc = useQueryClient();
  const findings = useQuery({ queryKey: ["did-findings"], queryFn: getDidFindings });
  const run = useMutation({
    mutationFn: runDidDiscovery,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["did-findings"] }),
  });
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ["did-findings"] });
    onChanged();
  };
  const items = findings.data?.items ?? [];
  return (
    <Panel title={`Discovery (${items.length} finding${items.length === 1 ? "" : "s"})`}>
      <p>
        The platform reconciles the fleet's ANI/E-ANI numbers against this table after every
        inventory refresh. Adoption backfills only what the evidence proves — carrier and E911
        registration are never guessed; findings with instructions need your out-of-band work
        plus a confirmed acknowledgement.
      </p>
      <div className="pairing-actions">
        <Button variant="secondary" disabled={run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? "Sweeping…" : "Run discovery now"}
        </Button>
      </div>
      {findings.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="ui-empty">Nothing to reconcile — fleet and platform agree.</div>
      ) : (
        items.map((f) => <FindingCard key={f.id} finding={f} onDone={refresh} />)
      )}
    </Panel>
  );
}

function FailoverReadinessPanel(): ReactNode {
  const readiness = useQuery({ queryKey: ["did-readiness"], queryFn: getFailoverReadiness });
  const items = readiness.data?.items ?? [];
  return (
    <Panel title="Failover readiness">
      <p>
        A DID is answerable after failover only when its alias exists on the backup account and the
        carrier failover lists the backup node. This checks the alias side (the carrier side is
        yours to confirm). Based on the last inventory refresh.
      </p>
      {readiness.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="ui-empty">No pair-assigned DIDs to check.</div>
      ) : (
        <table className="ui-table">
          <thead>
            <tr>
              <th>DID</th>
              <th>Backup</th>
              <th>On backup?</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.did_id}>
                <td>{it.number}</td>
                <td>
                  {it.backup} (node {it.backup_node_id})
                </td>
                <td>
                  <Badge tone={it.ready ? "online" : "offline"}>
                    {it.ready ? "ready" : "gap"}
                  </Badge>
                </td>
                <td>{it.instruction ? <span className="ui-empty">{it.instruction}</span> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function ImportPanel({ onImported }: { onImported: () => void }): ReactNode {
  const [csv, setCsv] = useState("");
  const [report, setReport] = useState<DidImportReport | null>(null);
  const dryRun = useMutation({
    mutationFn: () => importDids(csv, true),
    onSuccess: setReport,
  });
  const commit = useMutation({
    mutationFn: () => importDids(csv, false),
    onSuccess: (r) => {
      setReport(r);
      onImported();
    },
  });

  return (
    <Panel title="CSV import">
      <textarea
        aria-label="DID CSV"
        rows={5}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder="number,carrier,e911_location,notes"
      />
      <div className="pairing-actions">
        <Button variant="secondary" disabled={!csv || dryRun.isPending} onClick={() => dryRun.mutate()}>
          Validate (dry run)
        </Button>
        <Button disabled={!csv || commit.isPending} onClick={() => commit.mutate()}>
          Import
        </Button>
      </div>
      {report ? (
        <div className="did-import-report">
          <p>
            {report.created} created · {report.skipped} skipped
          </p>
          {report.problems.length > 0 ? (
            <Alert>
              <ul>
                {report.problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

export function DidsPage(): ReactNode {
  const canManage = usePermission("dids.manage");
  const qc = useQueryClient();
  const [assigning, setAssigning] = useState<number | null>(null);

  const dids = useQuery({ queryKey: ["dids"], queryFn: getDids, enabled: canManage });
  const pairs = useQuery({ queryKey: ["topo", "pairs"], queryFn: getPairs, enabled: canManage });

  const refresh = (): void => {
    setAssigning(null);
    void qc.invalidateQueries({ queryKey: ["dids"] });
  };

  if (!canManage) {
    return <Alert>DID management requires the dids.manage permission.</Alert>;
  }

  return (
    <div>
      <div className="dash__head">
        <h1>DIDs</h1>
        <a href={didsExportUrl()} download>
          Export CSV
        </a>
      </div>

      <Panel title={`Numbers (${dids.data?.items.length ?? 0})`}>
        {dids.isLoading ? (
          <Spinner />
        ) : (dids.data?.items.length ?? 0) === 0 ? (
          <div className="ui-empty">No DIDs imported yet.</div>
        ) : (
          <table className="ui-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Status</th>
                <th>Pair</th>
                <th>Carrier</th>
                <th>E911 location</th>
                <th>Confirmed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dids.data?.items.map((d) => (
                <Fragment key={d.id}>
                  <tr>
                    <td>{d.number}</td>
                    <td>
                      <Badge tone={DID_TONE[d.status] ?? "unknown"}>{d.status}</Badge>
                    </td>
                    <td>{d.pair_id ?? "—"}</td>
                    <td>{d.carrier ?? "—"}</td>
                    <td>{d.e911_location ?? "—"}</td>
                    <td>
                      <ConfirmCell did={d} onDone={refresh} />
                    </td>
                    <td>
                      <Button
                        variant="secondary"
                        onClick={() => setAssigning(assigning === d.id ? null : d.id)}
                      >
                        {assigning === d.id ? "Cancel" : "Assign"}
                      </Button>
                    </td>
                  </tr>
                  {assigning === d.id ? (
                    <AssignForm did={d} pairs={pairs.data?.items ?? []} onDone={refresh} />
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <DiscoveryPanel onChanged={refresh} />

      <FailoverReadinessPanel />

      <ImportPanel onImported={refresh} />
    </div>
  );
}
